<script setup>
// Google Ads ekranlarinin ortak tablosu.
//
// NEDEN tek bir tablo bileseni: dokuz ekranin sekizi ayni seyi yapiyor -
// bir ucu cagir, satirlari goster, olcumleri bicimle. Her birine ayri
// tablo yazmak ayni hatanin dokuz kez duzeltilmesi demekti.
//
// Sutunlar veri odakli tanimlaniyor:
//   { alan: 'harcama', anahtar: 'GOOGLE_ADS.SUTUN.HARCAMA', tur: 'para' }
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { sayi, para, yuzde, etiket, durumRengi } from './kopru.js';

const props = defineProps({
  sutunlar: { type: Array, required: true },
  satirlar: { type: Array, default: () => [] },
  paraBirimi: { type: String, default: '' },
  // Hangi alana gore siralanacagi; bos ise sunucudan geldigi sirada.
  varsayilanSiralama: { type: String, default: '' },
  bosMetin: { type: String, default: '' },
});

const { t } = useI18n();

const siralama = ref(props.varsayilanSiralama);
const artan = ref(false);

const siralanmis = computed(() => {
  if (!siralama.value) return props.satirlar;

  const kopya = [...props.satirlar];
  kopya.sort((a, b) => {
    const x = a[siralama.value];
    const y = b[siralama.value];
    if (typeof x === 'number' && typeof y === 'number') {
      return artan.value ? x - y : y - x;
    }
    return artan.value
      ? String(x ?? '').localeCompare(String(y ?? ''), 'tr')
      : String(y ?? '').localeCompare(String(x ?? ''), 'tr');
  });
  return kopya;
});

const siralamayiDegistir = sutun => {
  if (!sutun.siralanabilir) return;
  if (siralama.value === sutun.alan) {
    artan.value = !artan.value;
  } else {
    siralama.value = sutun.alan;
    artan.value = false;
  }
};

const hucre = (satir, sutun) => {
  const deger = satir[sutun.alan];
  switch (sutun.tur) {
    case 'para':
      return para(deger, props.paraBirimi);
    case 'yuzde':
      return yuzde(deger);
    case 'sayi':
      return sayi(deger);
    case 'etiket':
      return etiket(deger);
    default:
      return deger === null || deger === undefined || deger === ''
        ? '—'
        : String(deger);
  }
};

const sagaYasli = sutun => ['para', 'sayi', 'yuzde'].includes(sutun.tur);
</script>

<template>
  <div
    v-if="!satirlar.length"
    class="p-10 text-center border border-dashed rounded-xl border-n-weak"
  >
    <p class="text-sm text-n-slate-11">
      {{ bosMetin || t('GOOGLE_ADS.VERI_YOK') }}
    </p>
  </div>

  <div v-else class="overflow-x-auto border rounded-xl border-n-weak">
    <table class="w-full text-sm">
      <thead>
        <tr class="text-xs text-left border-b text-n-slate-11 border-n-weak">
          <th
            v-for="sutun in sutunlar"
            :key="sutun.alan"
            class="px-3 py-2 font-normal whitespace-nowrap"
            :class="[
              sagaYasli(sutun) ? 'text-right' : 'text-left',
              sutun.siralanabilir ? 'cursor-pointer hover:text-n-slate-12' : '',
            ]"
            @click="siralamayiDegistir(sutun)"
          >
            {{ t(sutun.anahtar) }}
            <span
              v-if="siralama === sutun.alan"
              class="inline-block ml-0.5 text-n-slate-10"
            >
              {{ artan ? '▲' : '▼' }}
            </span>
          </th>
          <th v-if="$slots.eylem" class="px-3 py-2" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(satir, i) in siralanmis"
          :key="satir.id || i"
          class="border-b last:border-0 border-n-weak hover:bg-n-alpha-1"
        >
          <td
            v-for="sutun in sutunlar"
            :key="sutun.alan"
            class="px-3 py-2 whitespace-nowrap"
            :class="sagaYasli(sutun) ? 'text-right tabular-nums' : ''"
          >
            <!-- Durum sutunu rozet olarak; goz taramasi kolaylassin. -->
            <span
              v-if="sutun.tur === 'durum'"
              class="px-2 py-0.5 text-xs rounded-md"
              :class="durumRengi(satir[sutun.alan])"
            >
              {{ etiket(satir[sutun.alan]) }}
            </span>
            <a
              v-else-if="sutun.tur === 'baglanti' && satir[sutun.alan]"
              :href="satir[sutun.alan]"
              target="_blank"
              rel="noopener"
              class="underline text-n-blue-11"
            >
              {{ satir[sutun.alan] }}
            </a>
            <span v-else :class="sutun.vurgu ? 'text-n-slate-12' : 'text-n-slate-11'">
              {{ hucre(satir, sutun) }}
            </span>
          </td>
          <td v-if="$slots.eylem" class="px-3 py-2 text-right">
            <slot name="eylem" :satir="satir" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
